import type { DetailedPinWithChecked } from '~/modules/design-board/component/all-pins/AllPins';
import { VotingControl } from './voting-control';
import { CommentButton } from '@modules/build-hub-comment/components/CommentButton';
import { useCallback, memo } from 'react';

import { useMoodboardItemDetailDrawer } from './item-detail-drawer';
import { useBuildHubBoardItemVoting } from '~/modules/build-hub/hooks/use-build-hub-board-item-voting';

import { ReactionTypeEnum } from '../../../../../../generated/graphql';

interface MoodboardItemActionBarProps {
    canVote: boolean;
    canComment: boolean;
    pin: DetailedPinWithChecked;
}

const MoodboardItemActionBarComponent = ({
    canVote,
    canComment,
    pin,
}: MoodboardItemActionBarProps) => {
    const { openDialog } = useMoodboardItemDetailDrawer();

    const {
        upVoteCollaborators,
        downVoteCollaborators,
        isVotedUp,
        isVotedDown,
        handleUpVote,
        handleDownVote,
        handleRevokeVote,
    } = useBuildHubBoardItemVoting({
        targetId: pin.ID,
    });

    const handleVoteUp = useCallback(() => {
        if (isVotedUp) {
            handleRevokeVote(ReactionTypeEnum.UpVote);
        } else {
            handleUpVote();
        }
    }, [isVotedUp, handleRevokeVote, handleUpVote]);

    const handleVoteDown = useCallback(() => {
        if (isVotedDown) {
            handleRevokeVote(ReactionTypeEnum.DownVote);
        } else {
            handleDownVote();
        }
    }, [isVotedDown, handleRevokeVote, handleDownVote]);

    const handleCommentClick = useCallback(
        (e: React.SyntheticEvent) => {
            e.stopPropagation();
            e.preventDefault();
            openDialog(pin);
        },
        [openDialog, pin]
    );

    return (
        <div className="flex flex-row items-start gap-2">
            {canComment && (
                <CommentButton
                    itemId={pin.ID}
                    commentsCount={pin.commentsCount}
                    inline
                    onClick={handleCommentClick}
                    className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2"
                />
            )}
            {canVote && (
                <VotingControl
                    hasVotedUp={isVotedUp}
                    hasVotedDown={isVotedDown}
                    upVoteCollaborators={upVoteCollaborators}
                    downVoteCollaborators={downVoteCollaborators}
                    onVoteUp={handleVoteUp}
                    onVoteDown={handleVoteDown}
                />
            )}
        </div>
    );
};

export const MoodboardItemActionBar = memo(MoodboardItemActionBarComponent);
